import { useMemo, useCallback, VFC } from 'react'
import type { CommonStoreState, SubLicense } from '../store'
import KeyValueDisplay from './common/KeyValueDisplay'
import { SuccessIcon } from '@panwds/react-ui'
import type { ActivationManifest } from '../renderer'

type DefaultPrimaryLicenseDetailsProps = {
  state: CommonStoreState
}

type LicenseVersionGroupType = {
  [key: string]: SubLicense[]
}
const QUANTITY_TITLE = 'Capacity'

const Activated = () => (
  <div>
    <SuccessIcon size='xs' />
    <span className='tw-ml-2'>Activated</span>
  </div>
)

const DefaultPrimaryLicenseDetails: VFC<DefaultPrimaryLicenseDetailsProps> = ({ state }) => {
  const licenseDisplayElements = useCallback((primaryAppId: string, primaryAppDef: ActivationManifest, licenses: SubLicense[], isNotLastIndex: boolean) => {
    const { purchased_size, is_csp_activated } = licenses?.find(e => e.app_id === primaryAppId) || {}
    const hasPurchasedSize = primaryAppDef?.flags?.show_purchased_size_as_qty
    let quantityAvailable: any = 0
    if (hasPurchasedSize) {
      quantityAvailable = is_csp_activated ? 0 : purchased_size
    }
    else {
      quantityAvailable = licenses?.filter((lic: { is_csp_activated: any; is_ela: any }) => !lic.is_csp_activated || lic.is_ela)?.length // don't filter if its ela license
    }
    const quantityTotal = hasPurchasedSize ? purchased_size : licenses?.length
    const unit = licenses[0]?.purchased_unit
    const licenseDisplayDetails = {
      title: licenses[0]?.license_display_name || licenses[0]?.license_type || '',
      data: [
        {
          label: QUANTITY_TITLE,
          value: `${quantityAvailable}/${quantityTotal} ${unit}`
        },
        ...(quantityAvailable === 0) ? [{
          label: 'activate',
          value: <Activated />,
          hideLabel: true
        }] : [],
      ],
    }
    return <KeyValueDisplay {...licenseDisplayDetails} showBottomDivider={isNotLastIndex} />
  }, [])

  const primaryAppDetails = useMemo(() => {
    const result: any[] = []
    const primaryAppDef = state?.manifest
    const primaryAppId = primaryAppDef?.primary_app_id || null
    if (primaryAppId && primaryAppDef) {
      const primaryLicenses = state.licenses?.get(primaryAppId)?.licenses || []
      if (primaryAppDef?.flags?.has_license_packages) {
        const groupByVersion = primaryLicenses.reduce((group, each) => {
          const license_version = each.license_version
          if (license_version) {
            group[license_version] = group[license_version] ?? []
            group[license_version].push(each)
          }
          return group
        }, {} as LicenseVersionGroupType)

        const groupByVersionValues = Object.values(groupByVersion)
        groupByVersionValues?.forEach((licenses: any, index: number) => {
          const isNotLastIndex = (groupByVersionValues.length - 1) !== index
          const response = licenseDisplayElements(primaryAppId, primaryAppDef, licenses, isNotLastIndex)
          result.push(response)
        })
      }
      else {
        const response = licenseDisplayElements(primaryAppId, primaryAppDef, primaryLicenses, false)
        result.push(response)
      }
    }
    return result
  }, [state?.manifest, state.licenses, licenseDisplayElements])


  return state?.loadingHashes || !primaryAppDetails.length ? <></> : (
    <>{primaryAppDetails.map(e => e)}</>
  )
}

export default DefaultPrimaryLicenseDetails
